import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AnalyticsAPI from '../../../../APIs/analyticsAPI';
import { DeliveryData } from '../types';

// Request parameters
export interface DeliveryCostAnalysisRequestParams {
  startDate?: string;
  endDate?: string;
  filter?: string;
  merchantId?: string;
  store?: string;
}

interface DeliveryCostAnalysisState {
  deliveryData: DeliveryData | null;
  loading: boolean;
  error: string | null;
}

const initialState: DeliveryCostAnalysisState = {
  deliveryData: null,
  loading: false,
  error: null,
};

// Async thunk to fetch delivery cost analysis data
export const fetchDeliveryCostAnalysis = createAsyncThunk<
  DeliveryData,
  DeliveryCostAnalysisRequestParams,
  { rejectValue: string }
>('deliveryCostAnalysis/fetchDeliveryCostAnalysis', async (params, { rejectWithValue }) => {
  try {
    const response = await AnalyticsAPI.getDeliveryCostAnalysis(params);
    const data: DeliveryData = response?.data || {};
    return data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch delivery cost analysis data'
    );
  }
});

const deliveryCostAnalysisSlice = createSlice({
  name: 'deliveryCostAnalysis',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDeliveryData: (state) => {
      state.deliveryData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch delivery cost analysis data
      .addCase(fetchDeliveryCostAnalysis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryCostAnalysis.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveryData = action.payload;
      })
      .addCase(fetchDeliveryCostAnalysis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.deliveryData = null;
      });
  },
});

export const { clearError, clearDeliveryData } = deliveryCostAnalysisSlice.actions;
export default deliveryCostAnalysisSlice.reducer;
